import { usePageData } from "@/lib/pageData";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { SITE_URL } from "@/lib/business";
import { serviceListSchema } from "@/lib/structuredData";
import { ServiceArea } from "@/components/ServiceArea";

export default function Services() {
  const initial = usePageData();
  const [services, setServices] = useState(initial?.directory || []);
  const [categories, setCategories] = useState(initial?.categories || []);
  const [loading, setLoading] = useState(!initial?.directory);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("category") || "";

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/services")])
      .then(([c, s]) => { setCategories(c.data); setServices(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return services.filter((s) => {
      if (cat && s.category_slug !== cat) return false;
      if (!needle) return true;
      return [s.name, s.short_description, s.category_name, ...(s.aliases || [])]
        .join(" ").toLowerCase().includes(needle);
    });
  }, [services, q, cat]);

  return (
    <div className="container-x py-14" data-testid="services-page">
      <SEO title="Business Services in India | Mynavodayans Services Directory"
           description="Explore business registration, licensing, compliance, websites and app development for businesses across India. Confirm the requirements for your location."
           canonicalPath="/services" noindex={Boolean(q.trim() || cat)} pageType="CollectionPage"
           jsonLd={[serviceListSchema(filtered, "/services", "Mynavodayans services directory"), {
             "@context": "https://schema.org", "@type": "BreadcrumbList",
             itemListElement: [
               { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
               { "@type": "ListItem", position: 2, name: "Services", item: SITE_URL + "/services" },
             ],
           }]} />
      <nav aria-label="Breadcrumb" className="text-xs text-slate-500" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#b07f0a]">Home</Link> <span aria-hidden="true">/</span> <span className="font-semibold text-slate-700">Services</span>
      </nav>
      <h1 className="mt-3 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">Business Services Across India</h1>
      <p className="mt-3 max-w-2xl text-slate-600">Browse our services for businesses across India, coordinated from Satna. Search by name or abbreviation — GST, FSSAI, PAN, MSME, DSC, IEC — or filter by category.</p>
      <nav className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-sm" aria-label="Browse service categories" data-testid="service-category-links">
        {categories.map((category) => <Link key={category.slug} to={`/services/category/${category.slug}`} className="font-semibold text-[#b07f0a] underline underline-offset-4">{category.name}</Link>)}
      </nav>

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
          <label htmlFor="service-search" className="sr-only">Search services</label>
          <input id="service-search" value={q}
                 onChange={(e) => setParams((p) => { e.target.value ? p.set("q", e.target.value) : p.delete("q"); return p; }, { replace: true })}
                 placeholder="Search services…" data-testid="services-search-input"
                 className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm focus:border-[#d4a017]" />
        </div>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2" role="group" aria-label="Filter by category" data-testid="category-filters">
        <button onClick={() => setParams((p) => { p.delete("category"); return p; }, { replace: true })}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-colors ${!cat ? "bg-[#0a192f] text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-[#d4a017]"}`}
                data-testid="filter-all">All</button>
        {categories.map((c) => (
          <button key={c.slug}
                  onClick={() => setParams((p) => { p.set("category", c.slug); return p; }, { replace: true })}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-colors ${cat === c.slug ? "bg-[#0a192f] text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-[#d4a017]"}`}
                  data-testid={`filter-${c.slug}`}>{c.name}</button>
        ))}
      </div>

      {loading ? (
        <p className="py-20 text-center text-slate-500" data-testid="services-loading">Loading services…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="services-empty-state">
          <p className="font-heading text-lg font-bold text-[#0a192f]">No services match "{q}"</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            Try a different name or abbreviation. Can't find what you need? <Link to="/contact" className="font-bold text-[#b07f0a] underline">Send us an enquiry</Link> — we may still be able to help.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {filtered.map((s) => (
            <article key={s.slug} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#d4a017]/60 hover:shadow-lg" data-testid={`service-card-${s.slug}`}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#b07f0a]">{s.category_name}</p>
              <h2 className="mt-2 font-heading text-lg font-bold leading-snug text-[#0a192f]">
                <Link to={`/services/${s.slug}`} className="hover:text-[#b07f0a]">{s.name}</Link>
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.short_description}</p>
              <div className="mt-4 flex gap-2">
                <Link to={`/services/${s.slug}`} className="text-sm font-bold text-[#0a192f] underline decoration-[#d4a017] decoration-2 underline-offset-4" data-testid={`service-details-${s.slug}`}>
                  Details
                </Link>
                <Link to={`/contact?service=${s.slug}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#0a192f] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#11294a]" data-testid={`service-enquire-${s.slug}`}>
                  Enquire <ArrowRight size={13} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
      <ServiceArea />
    </div>
  );
}
